import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

type OptionType = {
  id: number;
  label: string;
};

type AutoCompleteInputProps = {
  placeholder?: string;
  value: string;
  onChange: (val: string) => void;
  options: OptionType[];
  onSelect: (label: string, id: number) => void;
  loading?: boolean;
};

const AutoCompleteInput: React.FC<AutoCompleteInputProps> = ({
  placeholder = "Type to search...",
  value,
  onChange,
  options,
  onSelect,
  loading = false,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box position="relative" ref={wrapperRef}>
      <InputGroup>
        <Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setShowOptions(true); 
          }}
          onFocus={() => options.length > 0 && setShowOptions(true)}
        />
        <InputRightElement>
          {loading ? (
            <Spinner size="sm" />
          ) : (
            <IconButton
              aria-label="search"
              icon={<SearchIcon />}
              size="sm"
              variant="ghost"
            />
          )}
        </InputRightElement>
      </InputGroup>

      {/* Dropdown */}
      {showOptions && options.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px solid #ccc"
          borderRadius="md"
          zIndex={10}
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          {options.map((option) => (
            <Box
              key={option.id}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              onClick={() => {
                onSelect(option.label, option.id);
                setShowOptions(false);
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AutoCompleteInput;
